<template>
  <div class="page-view">
    <div class="table-wrap" :style="tableWrapStyle">
      <el-auto-resizer>
        <template #default="{ height, width }">
          <el-table-v2 :columns="columns" :data="data" :width="width" :height="height" fixed />
        </template>
      </el-auto-resizer>
    </div>
  </div>
  <el-dialog
    v-model="dialogVisible"
    title="扫码登录"
    width="500px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    :align-center="true"
  >
    <div style="text-align: center;">
      <div style="margin-bottom: 10px; color: #666; font-size: 14px;">
        当前设备: <span style="color: #409eff; font-weight: bold;">{{ deviceLabel(currentDevice) }}</span>
      </div>
      <el-image style="width: 200px; height: 200px;" :src="qrCodeUrl" fit="fill" />
      <div style="margin-top: 15px;">
        <el-input 
          v-model="verifyCode" 
          placeholder="请输入验证码" 
          style="width: 200px;"
          clearable
        />
      </div>
      <el-select
          v-model="proxyArea"
          placeholder="选择代理区域"
          style="width: 120px; margin-left: 8px;"
          size="small"
        >
          <el-option
            v-for="area in ProxyArea"
            :key="area"
            :label="area"
            :value="area"
          />
        </el-select>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="switchDevice">切换新设备</el-button>
        <el-button type="primary" @click="scanComplete()">
          已扫
        </el-button>
        <el-button @click="dialogVisible = false">取消</el-button>
      </span>
    </template>
  </el-dialog>
  <WebVerifyDialog
    v-model="webVerifyVisible"
    :url="followUpUrl"
    @fallback="followUpUrlVisible = true"
    @completed="scanComplete"
  />
  <UrlDialog v-model="followUpUrlVisible" :url="followUpUrl" />
</template>

<script setup lang="tsx">
import { computed, reactive, ref } from "vue";
import { getPlatforms, getQrCode, wechatLogin } from "../api/http";
import { ElButton, ElMessage, ElInput } from "element-plus";
import { ProxyArea } from "../api/util";
import UrlDialog from "../components/UrlDialog.vue";
import WebVerifyDialog from "../components/WebVerifyDialog.vue";
import {
  Device,
  DEFAULT_DEVICE,
  deviceLabel,
  type DeviceType,
  type Platform,
} from "../api/models";
import { useBreakpoint } from "../composables/useBreakpoint";

const { isMobile } = useBreakpoint();
const dialogVisible = ref(false);
const qrCodeUrl = ref('');
const verifyCode = ref('');
const currentDevice = ref<DeviceType>(DEFAULT_DEVICE);
const proxyArea = ref('安徽');
const followUpUrlVisible = ref(false);
const followUpUrl = ref('');
const webVerifyVisible = ref(false);
const deviceCellRenderer = ({ rowData }: { rowData: any }) => (
  <>
    <el-radio-group v-model={rowData.device} size="small">
      <el-radio label={Device.IPAD}>iPad</el-radio>
      <el-radio label={Device.MAC}>Mac</el-radio>
    </el-radio-group>
    <el-select
      v-model={rowData.proxyArea}
      placeholder="区域"
      style="width: 100px; margin-left: 6px;"
      size="small"
    >
      {ProxyArea.map((area) => (
        <el-option label={area} value={area} key={area} />
      ))}
    </el-select>
    {!rowData.proxyArea && (rowData.proxyArea = "安徽")}
  </>
);

const scanCellRenderer = ({ cellData: id, rowData }: { cellData: string; rowData: any }) => (
  <>
    <ElButton size="small" onClick={() => { scan(id, rowData.device, rowData.proxyArea); }}>扫码登录</ElButton>
  </>
);

const desktopColumns = [
  { key: "order", dataKey: "order", title: "#", width: 60, align: "center" },
  { key: "username", dataKey: "username", title: "Username", width: 160 },
  { key: "baseUrl", dataKey: "baseUrl", title: "Base Url", width: 260, flexGrow: 1 },
  { key: "callbackUrl", dataKey: "callbackUrl", title: "Callback Url", width: 320, flexGrow: 1 },
  { key: "device", dataKey: "device", title: "登录设备", width: 230, align: "center", cellRenderer: deviceCellRenderer },
  { key: "scan", dataKey: "_id", title: "Scan QR", width: 140, align: "center", fixed: "right" as const, cellRenderer: scanCellRenderer },
];

const mobileColumns = [
  { key: "username", dataKey: "username", title: "Username", width: 100, flexGrow: 1 },
  { key: "device", dataKey: "device", title: "设备", width: 200, align: "center", cellRenderer: deviceCellRenderer },
  { key: "scan", dataKey: "_id", title: "扫码", width: 90, align: "center", fixed: "right" as const, cellRenderer: scanCellRenderer },
];

const columns = computed(() => (isMobile.value ? mobileColumns : desktopColumns));

const data = ref<Platform[]>([]);

const tableWrapStyle = computed(() =>
  isMobile.value
    ? { height: 50 + Math.max(1, data.value.length) * 50 + "px", flex: "none", minHeight: "0" }
    : undefined
);
getPlatforms().then(res => {
  if (res.successful) {
    res.result.forEach((item, index) => {
      item.order = index + 1;
      // Preserve any device value already on the platform; only fall back
      // to the default for newly-fetched rows that have nothing set.
      if (item.device !== Device.MAC && item.device !== Device.IPAD) {
        item.device = DEFAULT_DEVICE;
      }
    });
    data.value = res.result;
  } else {
    ElMessage({
      message: res.message,
      type: "error",
    });
  }
});
const session = reactive({ platformId: "", wId: "" });

const scan = (id: string, device: DeviceType, area: string) => {
  session.platformId = id;
  currentDevice.value = device;
  verifyCode.value = ''; // 清空之前的验证码
  proxyArea.value = area;
  getQrCode(id, '', device, area).then(res => {
    if (res.successful) {
      session.wId = res.result.wId;
      qrCodeUrl.value = res.result.qrCodeUrl;
      dialogVisible.value = true;
    } else {
      ElMessage({
        message: res.message,
        type: "error",
      });
    }
  }).catch(error => {
    ElMessage({
      message: "获取二维码失败\n" + error.message,
      type: "error",
    });
  });
}

const switchDevice = () => {
  scan(session.platformId, Device.MAC, proxyArea.value);
}

const scanComplete = () => {
  wechatLogin(session.platformId, session.wId, verifyCode.value, proxyArea.value, currentDevice.value).then(res => {
    if (res.successful) {
      if (typeof res.result === "string") {
        // 平台要求通过后续链接完成图形验证。先尝试「自动验证」(服务端解密 +
        // 无头伪微信浏览器,运维只需在弹窗里拖一下滑块);失败会 @fallback 回退到
        // UrlDialog 手动扫码。@completed 时重跑 scanComplete 拉取登录结果。
        followUpUrl.value = res.result;
        webVerifyVisible.value = true;
      } else if (res.result.wcId) {
        dialogVisible.value = false;
        ElMessage({
          message: "登录成功",
          type: "success",
        });
      }
    } else {
      ElMessage({
        message: res.message,
        type: "error",
      });
    }
  }).catch(() => {
    ElMessage({
      message: "登录失败",
      type: "error",
    });
  });
}
</script>
